import type { BottomSheetModal } from "@gorhom/bottom-sheet";
import BottomSheetModalWrapper, {
	type CustomHandle,
} from "@memorang/ui/components/BottomSheetModalWrapper";
import Box from "@memorang/ui/components/Box";
import { TextSkeleton } from "@memorang/ui/components/ChatUI/TextSkeleton";
import DialogWrapper from "@memorang/ui/components/DialogWrapper";
import { useDevice } from "@memorang/ui/useDevice";
import Icon from "@react-native-vector-icons/material-design-icons/static";
/* -----------------Icons--------------- */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type StyleProp, StyleSheet, type ViewStyle } from "react-native";
import { Dialog, Text } from "react-native-paper";
import type { FeedbackParams } from "../../feedback/types";
import { SummaryActions } from "../../qa-search/SummaryActions";
import type { HandleTracking } from "../../types";
import { events } from "../constants";
import type { SessionMetadata } from "../types";
import FullReportContent from "./FullReportContent";
import { FullReportHandle } from "./FullReportHandle";

type Props = {
	sessionAnalysisData: string;
	visible: boolean;
	handleDismiss: () => void;
	feedbackDialogParams?: FeedbackParams;
	handleTracking: HandleTracking;
	bottomContent?: React.ReactNode;
	sessionMetadata?: SessionMetadata;
	streaming: boolean;
	loading: boolean;
	appFont?: string;
	fontSize?: number;
	style?: StyleProp<ViewStyle>;
};

const FullReportDialog = ({
	sessionAnalysisData,
	visible,
	handleDismiss,
	feedbackDialogParams,
	handleTracking,
	bottomContent,
	sessionMetadata,
	streaming,
	loading,
	appFont,
	fontSize,
	style,
}: Props) => {
	const [openTime, setOpenTime] = useState(0);
	const bottomSheetRef = useRef<BottomSheetModal>(null);
	const { isMobile } = useDevice();
	const { t } = useTranslation();

	useEffect(() => {
		if (visible && !streaming) {
			setOpenTime(Date.now());
			handleTracking(events.fullReportViewed);
		}
	}, [handleTracking, visible, streaming]);

	const handleOnDismiss = () => {
		const readingTimeInSeconds = Math.floor((Date.now() - openTime) / 1000);
		handleTracking(events.readingTime, {
			readTime: `${readingTimeInSeconds} seconds`,
		});
		handleDismiss();
	};

	const CustomHandle = useCallback<CustomHandle>(
		(handleExpandCollapse) => {
			return (
				<FullReportHandle
					handleExpandCollapse={handleExpandCollapse}
					sessionAnalysisData={sessionAnalysisData}
					handleTracking={handleTracking}
					feedbackDialogParams={feedbackDialogParams}
					sessionMetadata={sessionMetadata}
				/>
			);
		},
		[
			feedbackDialogParams,
			handleTracking,
			sessionAnalysisData,
			sessionMetadata,
		],
	);

	const reportContent = loading ? (
		<Box paddingHorizontal={isMobile ? 0 : 16}>
			<TextSkeleton numLines={8} height={18} />
		</Box>
	) : (
		<FullReportContent
			bottomContent={bottomContent}
			sessionAnalysisData={sessionAnalysisData}
			appFont={appFont}
			fontSize={fontSize}
		/>
	);

	if (isMobile) {
		return (
			<BottomSheetModalWrapper
				showModal={visible}
				snapPoints={["90%"]}
				ref={bottomSheetRef}
				snapIndex={0}
				onDismiss={handleOnDismiss}
				animateOnMount
				enableDismissOnClose
				enablePanDownToClose
				customHandle={CustomHandle}
			>
				{reportContent}
			</BottomSheetModalWrapper>
		);
	}

	const { sessionDate, sessionTitle, numItems } = sessionMetadata ?? {};

	return (
		<DialogWrapper
			visible={visible}
			handleClose={handleOnDismiss}
			showCloseButton
			style={StyleSheet.flatten([
				{
					width: "100%",
					minHeight: "60%",
				},
				style,
			])}
		>
			<Dialog.Icon
				icon={(props) => (
					<Icon
						{...props}
						name="star-four-points-outline"
						size={24}
						accessibilityLabel="Analysis and Insights"
					/>
				)}
			/>
			<Dialog.Title
				style={{
					textAlign: "center",
					marginBottom: 0,
				}}
			>
				{t("report.sessionAnalysis.title")}
			</Dialog.Title>
			{sessionMetadata && (
				<Box alignItems="center" paddingBottom={16}>
					<Text>{sessionTitle}</Text>
					<Text
						variant="bodySmall"
						style={{
							fontStyle: "italic",
						}}
					>
						{`${numItems} ${t("common.item", { count: numItems })} · ${sessionDate}`}
					</Text>
				</Box>
			)}
			<Dialog.ScrollArea
				style={{
					paddingHorizontal: 0,
				}}
			>
				{reportContent}
			</Dialog.ScrollArea>
			<Dialog.Actions
				style={{
					alignItems: "flex-end",
				}}
			>
				<SummaryActions
					align="flex-end"
					showEmail
					text={sessionAnalysisData}
					handleTracking={handleTracking}
					streaming={streaming}
					feedbackDialogParams={feedbackDialogParams}
				/>
			</Dialog.Actions>
		</DialogWrapper>
	);
};

export default FullReportDialog;
